import React from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Box, Typography, Stack, Button } from "@mui/material";
import { Business, Settings } from "@mui/icons-material";

// Layouts
import AdminLayout from "../components/admin/AdminLayout";

// Rutas protegidas
import { 
    BusinessAdminRoute, 
    SystemAdminRoute, 
    PermissionRoute 
} from "../components/routes/ProtectedRoute";

// Componentes de Admin de Negocio
import BusinessDashboard from "../pages/admin/business/BusinessDashboard";
import UserManagement from "../pages/admin/business/UserManagement";
import BusinessLogs from "../pages/admin/business/BusinessLogs";
import TempComponent from "../components/TempComponent";

// Componentes de Admin de Sistema
import SystemDashboard from "../pages/admin/system/SystemDashboard";
import SystemLogs from "../pages/admin/system/SystemLogs";
import SerializationDashboard from "../components/SerializationDashboard";
import EntityTraceabilityDashboard from "../components/admin/EntityTraceabilityDashboard";

// Permisos
import { PERMISSIONS } from "../utils/permissions";

/**
 * Componente selector de tipo de administración
 */
const AdminTypeSelector = () => {
    const navigate = useNavigate();
    
    return (
        <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
                Seleccionar Tipo de Administración
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center">
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<Business />}
                    onClick={() => navigate("/admin/business")}
                >
                    Admin de Negocio
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<Settings />}
                    onClick={() => navigate("/admin/system")}
                >
                    Admin de Sistema
                </Button>
            </Stack>
        </Box>
    );
};

/**
 * Configuración de rutas para Admin de Negocio
 * Rutas exactas según menuConfigs.js
 */
const BusinessAdminRoutes = () => (
    <Routes>
        {/* Dashboard */}
        <Route 
            path="/" 
            element={
                <BusinessAdminRoute>
                    <BusinessDashboard />
                </BusinessAdminRoute>
            } 
        />
        
        {/* Gestión de Usuarios */}
        <Route 
            path="/users" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_USERS}>
                    <UserManagement />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/agents" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_AGENTS}>
                    <TempComponent title="Gestión de Agentes" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/kyc-pending" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_KYC}>
                    <TempComponent title="KYC Pendientes" />
                </PermissionRoute>
            } 
        />
        
        {/* Oportunidades */}
        <Route 
            path="/opportunities" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_OPPORTUNITIES}>
                    <TempComponent title="Gestión de Oportunidades" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/project-validation" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_OPPORTUNITIES}>
                    <TempComponent title="Validación de Proyectos" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/project-review" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_OPPORTUNITIES}>
                    <TempComponent title="Revisión de Proyectos" />
                </PermissionRoute>
            } 
        />
        
        {/* Financiero */}
        <Route 
            path="/capital-management" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_CAPITAL}>
                    <TempComponent title="Gestión de Capital" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/distributions" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_CAPITAL}>
                    <TempComponent title="Distribuciones" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/commissions" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_COMMISSIONS}>
                    <TempComponent title="Comisiones" />
                </PermissionRoute>
            } 
        />
        
        {/* Reportes */}
        <Route 
            path="/investment-reports" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.VIEW_BUSINESS_REPORTS}>
                    <TempComponent title="Reportes de Inversión" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/agent-reports" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.VIEW_BUSINESS_REPORTS}>
                    <TempComponent title="Reportes de Agentes" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/compliance-reports" 
            element={
                <PermissionRoute permission={PERMISSIONS.AUDIT.VIEW_COMPLIANCE_REPORTS}>
                    <TempComponent title="Reportes de Compliance" />
                </PermissionRoute>
            } 
        />
        
        {/* Compliance & Legal */}
        <Route 
            path="/user-audit" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.AUDIT_USER_ACTIONS}>
                    <TempComponent title="Auditoría de Usuarios" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/legal-documents" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_KYC}>
                    <TempComponent title="Documentos Legales" />
                </PermissionRoute>
            } 
        />
        
        {/* Offboarding */}
        <Route 
            path="/offboarding" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.MANAGE_OFFBOARDING}>
                    <TempComponent title="Offboarding" />
                </PermissionRoute>
            } 
        />
        
        {/* Logs de Negocio */}
        <Route 
            path="/logs" 
            element={
                <PermissionRoute permission={PERMISSIONS.BUSINESS.VIEW_BUSINESS_REPORTS}>
                    <BusinessLogs />
                </PermissionRoute>
            } 
        />
        
        {/* Ruta por defecto */}
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
);

/**
 * Configuración de rutas para Admin de Sistema
 * Rutas exactas según menuConfigs.js
 */
const SystemAdminRoutes = () => (
    <Routes>
        {/* Dashboard */}
        <Route 
            path="/" 
            element={
                <SystemAdminRoute>
                    <SystemDashboard />
                </SystemAdminRoute>
            } 
        />
        
        {/* Serialización */}
        <Route 
            path="/serialization" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.MANAGE_SERIALIZATION}>
                    <SerializationDashboard />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/traceability" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.VIEW_TRACEABILITY}>
                    <EntityTraceabilityDashboard />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/migrations" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.MANAGE_MIGRATIONS}>
                    <TempComponent title="Gestión de Migraciones" />
                </PermissionRoute>
            } 
        />
        
        {/* Monitoreo */}
        <Route 
            path="/logs" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.VIEW_SYSTEM_LOGS}>
                    <SystemLogs />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/monitoring/performance" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.VIEW_PERFORMANCE}>
                    <TempComponent title="Rendimiento" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/monitoring/errors" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.VIEW_SYSTEM_LOGS}>
                    <TempComponent title="Seguimiento de Errores" />
                </PermissionRoute>
            } 
        />
        
        {/* Desarrollo */}
        <Route 
            path="/dev-tools" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.ACCESS_DEV_TOOLS}>
                    <TempComponent title="Herramientas de Desarrollo" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/api" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.ACCESS_DEV_TOOLS}>
                    <TempComponent title="Gestión de APIs" />
                </PermissionRoute>
            } 
        />
        
        {/* Backup y Recuperación */}
        <Route 
            path="/backup" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.MANAGE_BACKUPS}>
                    <TempComponent title="Gestión de Backups" />
                </PermissionRoute>
            } 
        />
        
        <Route 
            path="/backup/recovery" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.MANAGE_BACKUPS}>
                    <TempComponent title="Herramientas de Recuperación" />
                </PermissionRoute>
            } 
        />
        
        {/* Configuración */}
        <Route 
            path="/settings" 
            element={
                <PermissionRoute permission={PERMISSIONS.SYSTEM.CONFIGURE_SYSTEM}>
                    <TempComponent title="Configuración del Sistema" />
                </PermissionRoute>
            } 
        />
        
        {/* Ruta por defecto */}
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
);

/**
 * Rutas principales de administración
 */
const AdminRoutes = () => (
    <Routes>
        {/* Selector de tipo de admin */}
        <Route path="/selector" element={<AdminTypeSelector />} />
        
        {/* Admin de Negocio */}
        <Route path="/business/*" element={
            <BusinessAdminRoute>
                <AdminLayout>
                    <BusinessAdminRoutes />
                </AdminLayout>
            </BusinessAdminRoute>
        } />
        
        {/* Admin de Sistema */}
        <Route path="/system/*" element={
            <SystemAdminRoute>
                <AdminLayout>
                    <SystemAdminRoutes />
                </AdminLayout>
            </SystemAdminRoute>
        } />
        
        {/* Ruta por defecto - redirigir a selector de tipo */}
        <Route path="*" element={<Navigate to="/admin/selector" replace />} />
    </Routes>
);

export default AdminRoutes; 